// Окно связей работы (владелец 2026-10-06/07): открывается двойным щелчком по блоку на схеме
// связей в режиме «Иерархия» «Модели ГПР». Два поля — «Предшественник» и «Последователь»
// (выпадающие списки работ проекта): выбрали работу — связь создана; выбранные работы
// стоят тегами под полями, щелчок по тегу — тип, задержка, сдвиг этажей, мягкая, удалить
// (окно `EdgePop` с экрана «Связи»), крестик на теге — убрать связь.
// Данные — те же строки шаблонов связей проекта, что на экране «Связи» и в карточке работы.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { call, p, type Data } from './api';
import { Pop, at, type Anchor } from './Pop';
import { VyborSpiska } from './Podskazki';
import { EdgePop, svyazSlovami, type Edge } from './screens/Links';

type Tpl = Data<'/api/link-templates', 'get'>[number];
type TplRow = Data<'/api/link-templates/{tid}/rows', 'get'>[number];

export function SvyaziRaboty({ pid, name, raboty, can, at: a, onClose, onChanged, onFail }: {
  pid: number; name: string; raboty: string[]; can: boolean; at: Anchor;
  onClose: () => void; onChanged: () => void; onFail: (m: string) => void;
}) {
  const [rows, setRows] = useState<(TplRow & { tpl: string })[]>([]);
  const [tid, setTid] = useState(0);
  const [edge, setEdge] = useState<{ e: Edge; at: Anchor } | null>(null);

  const load = useCallback(async () => {
    const t = await call<Tpl[]>('get', '/api/link-templates?project=' + pid);
    const tpls = t.ok ? t.data : [];
    const vse = await Promise.all(tpls.map(async x => {
      const r = await call<TplRow[]>('get', p('/api/link-templates/{tid}/rows', { tid: x.id }));
      return r.ok ? r.data.map(row => ({ ...row, tpl: x.name })) : [];
    }));
    setRows(vse.flat());
    // новые связи — в шаблон, где их больше всего; нет ни одного — «Базовый» (создаётся при первой связи)
    const glavnyj = tpls.slice().sort((x, y) => y.svyazey - x.svyazey).find(x => x.svyazey > 0)
      || tpls.find(x => x.name.toLowerCase() === 'базовый');
    setTid(glavnyj?.id || 0);
  }, [pid]);
  useEffect(() => { void load(); }, [load]);

  const edges: Edge[] = useMemo(() => rows.map(r => ({ id: r.id, work: r.work, pred: r.pred, tip: r.tip,
    zapas: r.zapas, operezhenie: r.operezhenie, soft: !!r.soft, kom: r.kom || '', tpl: r.tpl })), [rows]);
  const preds = edges.filter(e => e.work === name);
  const next = edges.filter(e => e.pred === name);

  const posle = async (r: { ok: boolean; error?: { message: string } }) => {
    if (!r.ok) { onFail(r.error?.message || 'Не сохранилось'); return; }
    await load();
    onChanged();
  };
  /** выбор в поле — связь «после окончания» сразу; тип и задержка — щелчком по тегу */
  async function dobavit(kuda: 'pred' | 'next', vybr: string) {
    if (!vybr || vybr === name) return;
    let t = tid;
    if (!t) {
      const c = await call<{ id: number }>('post', '/api/link-templates', { name: 'Базовый', project: pid });
      if (!c.ok) { onFail(c.error.message); return; }
      t = c.data.id;
    }
    const [work, pred] = kuda === 'pred' ? [name, vybr] : [vybr, name];
    await posle(await call('post', p('/api/link-templates/{tid}/rows', { tid: t }), { work, pred }) as never);
  }
  async function saveEdge(e: Edge, v: { tip: string; zapas: string; oper: string; soft: boolean; kom: string }) {
    setEdge(null);
    await posle(await call('patch', p('/api/template-rows/{rid}', { rid: e.id as number }),
      { tip: v.tip, zapas: v.zapas || 0, operezhenie_etazhej: v.oper || 0, soft: v.soft, kom: v.kom }) as never);
  }
  async function delEdge(e: Edge) {
    setEdge(null);
    await posle(await call('delete', p('/api/template-rows/{rid}', { rid: e.id as number })) as never);
  }

  const pole = (zag: string, kuda: 'pred' | 'next', lst: Edge[], kogo: 'pred' | 'work') => {
    const zanyato = new Set(lst.map(e => e[kogo]));
    const varianty = raboty.filter(w => w !== name && !zanyato.has(w));
    return (
      <div className="sr-pole">
        <div className="rp-zag">{zag}</div>
        {can && <VyborSpiska value="" opts={varianty} placeholder={kuda === 'pred' ? 'после какой работы…' : 'какая работа идёт следом…'}
          title="Выберите работу — связь «после окончания» появится сразу; тип и задержку меняют щелчком по тегу"
          style={{ width: '100%' }} onPick={v => void dobavit(kuda, v)} />}
        <div className="sr-tegi">
          {lst.length ? lst.map(e => (
            <span key={String(e.id)} className={'sr-teg' + (e.soft ? ' myag' : '') + (can ? ' prav' : '')}
              title={svyazSlovami(e) + (can ? ' · щелчок — тип, задержка, сдвиг этажей, мягкая' : '')}
              onClick={ev => { if (can) setEdge({ e, at: at(ev) }); }}>
              <b>{e[kogo]}</b>
              <span className="sr-teg-sv">{svyazSlovami(e)}</span>
              {can && <a className="sr-teg-x" title="Убрать связь" onClick={ev => { ev.stopPropagation(); void delEdge(e); }}>×</a>}
            </span>)) : <span className="pw-muted">нет</span>}
        </div>
      </div>);
  };

  return (
    <>
      <Pop at={a} title={'Связи · ' + name} onClose={onClose} width={560}>
        {pole('Предшественник', 'pred', preds, 'pred')}
        {pole('Последователь', 'next', next, 'work')}
        <div className="pw-muted" style={{ marginTop: 8 }}>Связи действуют на все этажи и корпуса работы. Даты графика — после «Пересчитать».</div>
      </Pop>
      {edge && <EdgePop at={edge.at} e={edge.e} onClose={() => setEdge(null)}
        onSave={v => void saveEdge(edge.e, v)} onDelete={() => void delEdge(edge.e)} />}
    </>
  );
}
